import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button, FormHelperText, Stack } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    ControlledInput,
    formatDateString,
    IIntake,
    IIntakeCompletionForm,
    Section,
    StatusCode,
    useSelectedOrganisation,
    Widget,
} from '../../../../shared';
import { IntakeCreateSupportPeriod, IntakeSummary } from '../../../components';
import { useCompleteIntake } from '../../../hooks';
import { useIntakeIncomeResult } from '../../../mappers';
import {
    intakeCompletionFormFormMapper,
    intakeCompletionToFormMapper,
} from '../../../mappers/intake-completion.mapper';
import { useIntakeCompletionSchema } from '../../../validators';
import { AxiosError, AxiosResponse } from 'axios';

interface Props {
    intake?: IIntake;
}

export const IntakeCompletionPage: FC<Props> = ({ intake }) => {
    const { t } = useTranslation();
    const [duplicateError, setDuplicateError] = useState<string | undefined>(undefined);
    const navigate = useNavigate();
    const { organisation } = useSelectedOrganisation();

    const schema = useIntakeCompletionSchema();
    const form = useForm<IIntakeCompletionForm>({
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    });

    const { mutateAsync: completeIntake, isPending } = useCompleteIntake();
    const intakeIncomeResult = useIntakeIncomeResult(intake);

    useEffect(() => {
        if (organisation && intake) form.reset(intakeCompletionToFormMapper(organisation, intake, intakeIncomeResult));
    }, [form, organisation, intake, intakeIncomeResult, form.reset]);

    const onSubmit = async (item: IIntakeCompletionForm) => {
        try {
            if (intake) {
                await completeIntake({
                    supportCaseId: intake.supportCaseId,
                    intakeId: intake.id,
                    item: intakeCompletionFormFormMapper(item),
                });
                setDuplicateError(undefined);
                form.clearErrors();
                navigate('../../intakes');
            }
        } catch (err) {
            if ((err as AxiosError)?.response?.status === StatusCode.CONFLICT) {
                const { data } = (err as AxiosError)?.response?.data as AxiosResponse<{
                    startDate: string;
                    endDate: string;
                }>;

                form.setError('startDate', {});
                form.setError('endDate', {});
                setDuplicateError(
                    t('duplicateSupportPeriodError', {
                        start: formatDateString(data?.startDate),
                        end: formatDateString(data?.endDate),
                        interpolation: { escapeValue: false },
                    }),
                );
            } else {
                throw err;
            }
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate={true} autoComplete="off">
                <Widget
                    footer={
                        <Stack direction="row" justifyContent="space-between">
                            <Button
                                startIcon={<ChevronLeft />}
                                onClick={() =>
                                    intake?.externalIntake ? navigate('../intro') : navigate('../expenses')
                                }
                                disabled={isPending}
                            >
                                {t('previous')}
                            </Button>
                            <Button
                                endIcon={<ChevronRight />}
                                variant="contained"
                                color="primary"
                                onClick={form.handleSubmit((data) => onSubmit(data))}
                                disabled={isPending}
                            >
                                {t('complete')}
                            </Button>
                        </Stack>
                    }
                >
                    <Stack spacing={2}>
                        <IntakeSummary intake={intake} />
                        <IntakeCreateSupportPeriod form={form} />
                        <Section title={t('comments')}>
                            <ControlledInput name="comment" multiline />
                        </Section>
                        {duplicateError !== undefined && (
                            <FormHelperText sx={{ color: (theme) => theme.palette.error.main }}>
                                {duplicateError || t('shared.form.fallbackError')}
                            </FormHelperText>
                        )}
                    </Stack>
                </Widget>
            </form>
        </FormProvider>
    );
};
